import React, {Component, type ChangeEvent, type KeyboardEvent} from 'react';
import I18n from 'app/i18n';
import {inject, observer} from 'mobx-react';
import Recipe, {type MarketplaceRecipeProps} from './recipe';
import Pagination from 'app/components/pagination';
import {getLinkPath, userSignedIn} from 'app/utils';
import {recipeStore} from 'app/store/global';
import SellOrders from './sell_orders';
import BuyOrders from './buy_orders';
import SearchInput from 'app/components/SearchInput';
import EmptyResults from 'app/components/articles/EmptyResults';
import Get, {type ParamsInterface} from 'app/utils/Get';
import SmartPageLoader from 'app/components/reusable/smart_loaders/SmartPageLoader';
import {locationQuery} from 'app/utils/navigate/location';
import {KEY_ENTER} from 'app/utils/Consts';
import SlotFilters, {type MarketplaceSlotPart} from './SlotFilters';
import type PaginateInterface from 'app/interfaces/articles/PaginateInterface';

interface RecipesState {
    bodyParts: MarketplaceSlotPart[]
    loading: boolean
    searchValue: string
    submittedSearch: string
}

interface MarketplaceRecipesData {
    paginate?: Partial<PaginateInterface>
    recipes?: MarketplaceRecipeProps[]
}

interface RecipesProps {
    recipeStore?: MarketplaceRecipesData
}

@inject('recipeStore')
@observer
export default class Recipes extends Component<RecipesProps, RecipesState> {
    state: RecipesState = {
        bodyParts: [],
        loading: true,
        searchValue: '',
        submittedSearch: ''
    };

    hasLoaded = false;

    requestId = 0;

    componentDidMount(): void {
        this.switchPage({page: locationQuery().page || 1});
    }

    switchPage = (params: ParamsInterface, searchValue = ''): void => {
        const requestId = this.beginRequest();

        new Get({
            params: this.requestParams(params, searchValue),
            url: getLinkPath('/shop/marketplace')
        })
            .execute()
            .then((response: Response) => response.json())
            .then((response: MarketplaceRecipesData) => this.applyRecipes(response, requestId));
    };

    beginRequest = (): number => {
        this.requestId += 1;

        if (!this.hasLoaded) {
            this.setState({loading: true});
        }

        return this.requestId;
    };

    requestParams = (params: ParamsInterface, searchValue: string): ParamsInterface => {
        const requestParams: ParamsInterface = {...params, search: searchValue};
        const {bodyParts} = this.state;

        if (bodyParts.length > 0) {
            requestParams.body_parts = bodyParts;
        }

        return requestParams;
    };

    applyRecipes = (response: MarketplaceRecipesData, requestId: number): void => {
        if (requestId !== this.requestId) {
            return;
        }

        recipeStore.updateRecipes({
            paginate: response.paginate ?? null,
            recipes: response.recipes ?? null
        });
        this.hasLoaded = true;
        this.setState({loading: false});
    };

    searchHandle = (event: KeyboardEvent<HTMLInputElement>): void => {
        if (event.key !== KEY_ENTER) {
            return;
        }

        const submittedSearch = event.currentTarget.value;

        this.setState({submittedSearch});
        this.switchPage({page: 1}, submittedSearch);
    };

    searchValueHandler = (event: ChangeEvent<HTMLInputElement>): void => {
        this.setState({searchValue: event.currentTarget.value});
    };

    handleRefreshSearch = (): void => {
        this.setState({searchValue: '', submittedSearch: ''});
        this.switchPage({page: 1}, '');
    };

    handleBodyPartsChange = (bodyParts: MarketplaceSlotPart[]): void => {
        this.setState({bodyParts}, () => {
            this.switchPage({page: 1}, this.state.submittedSearch);
        });
    };

    render(): React.ReactElement {
        const {recipes = [], paginate} = this.props.recipeStore ?? recipeStore;
        const {loading, bodyParts, searchValue, submittedSearch} = this.state;

        return <div className="market">
            {userSignedIn()
                ? <div className="tabs js-tabs tabs--lots">
                    <div className="tabs__content">
                        <div className="tabs__content-item">
                            <SellOrders/>
                            <BuyOrders/>
                        </div>
                    </div>
                </div>
                : null
            }
            <div className="grid-header-marketplace background__grey vertical-center">
                <div className="grid-row market-filters">
                    <SearchInput
                        classSearchInput="market-filters__search"
                        handleChange={this.searchValueHandler}
                        handleKeyPress={this.searchHandle}
                        value={searchValue}
                        onClick={this.handleRefreshSearch}
                    />
                    <SlotFilters
                        selected={bodyParts}
                        onChange={this.handleBodyPartsChange}
                    />
                </div>
            </div>
            <div className="market__items__heading tables-heading">
                <div className="market__items__name-quantity">
                    <div className="market__items__name">
                        {I18n.t('user.name')}
                    </div>
                    <div className="market__items__quantity">
                        {I18n.t('quantity')}
                    </div>
                </div>
                <div className="market__items__price-col">
                    <div className="market__items__price-title">
                        {I18n.t('price')}
                    </div>
                </div>
            </div>
            {loading
                ? <div className="table-more">
                    <SmartPageLoader/>
                </div>
                : <div className="market__items js-table">
                    {recipes.length > 0
                        ? recipes.map((recipe: MarketplaceRecipeProps) => <Recipe key={recipe.id} {...recipe}/>)
                        : <EmptyResults text={I18n.t('empty_marketplace_page')}/>}
                </div>}
            <Pagination {...paginate} searchValue={submittedSearch} switchPage={this.switchPage}/>
        </div>;
    }
}
